// src/pages/public/SuppliersPage.jsx
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Search, Star, Package } from 'lucide-react'
import { storefrontApi } from '../../api/services'
import { PageSpinner, Pagination } from '../../components/common/ui'

export default function SuppliersPage() {
  const [sp, setSp] = useSearchParams()
  const q = sp.get('q') || ''
  const page = parseInt(sp.get('page') || '1')

  const { data, isLoading } = useQuery({
    queryKey: ['storefronts', q, page],
    queryFn: () => storefrontApi.index({ q, page }),
  })

  const suppliers = data?.data?.data?.data || data?.data?.data || []
  const lastPage = data?.data?.data?.last_page || 1

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-display font-bold text-gray-900">Fabric Suppliers</h1>
        <p className="text-muted mt-2">Connect directly with verified Indian textile manufacturers and weavers.</p>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
        <input
          value={q}
          onChange={e => { const n = new URLSearchParams(); if (e.target.value) n.set('q', e.target.value); setSp(n) }}
          placeholder="Search suppliers, specialties…"
          className="input pl-9 w-full"
        />
      </div>

      {isLoading && <PageSpinner />}

      {!isLoading && suppliers.length === 0 && (
        <div className="py-16 text-center text-muted">No suppliers found{q ? ` for "${q}"` : ''}.</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {suppliers.map(s => (
          <Link key={s.id} to={`/suppliers/${s.slug}`} className="group bg-white border border-border rounded-2xl overflow-hidden hover:border-pink hover:shadow-md transition-all">
            <div className="h-32 bg-gradient-brand-soft overflow-hidden relative">
              {s.banner_image && <img src={s.banner_image} alt="" className="w-full h-full object-cover opacity-60" />}
              {s.logo_image && (
                <div className="absolute bottom-3 left-4 w-14 h-14 rounded-xl overflow-hidden border-2 border-white shadow">
                  <img src={s.logo_image} alt={s.display_name} className="w-full h-full object-cover" />
                </div>
              )}
            </div>
            <div className="p-4 pt-3">
              <h3 className="font-bold text-gray-900 group-hover:text-pink transition-colors">{s.display_name || s.business_name}</h3>
              {s.city && <p className="text-xs text-muted mt-0.5">📍 {s.city}{s.state ? `, ${s.state}` : ''}</p>}
              {s.tagline && <p className="text-xs text-gray-600 mt-1.5 line-clamp-2">{s.tagline}</p>}
              <div className="flex items-center gap-3 mt-3 text-xs text-muted">
                <span className="flex items-center gap-1"><Package size={12} /> {s.products_count ?? 0} products</span>
                {s.avg_rating && <span className="flex items-center gap-1"><Star size={12} className="fill-yellow-400 text-yellow-400" /> {Number(s.avg_rating).toFixed(1)}</span>}
                {s.gi_certified && <span className="text-green-600 font-bold">GI ✓</span>}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {lastPage > 1 && (
        <div className="mt-8">
          <Pagination currentPage={page} lastPage={lastPage} onPageChange={p => { const n = new URLSearchParams(sp); n.set('page', p); setSp(n) }} />
        </div>
      )}
    </div>
  )
}
